import {
    useTransition,
    useRef,
    useCallback,
    useState,
    useMemo,
    useEffect
} from "react";

import { graphql, useRefetchableFragment } from "react-relay";
import { Initializer } from "src/analytics/atl-analytics/initializer";

import { ContentPlatformContentQueryInput } from "@/__generated__/templatePaginatedSearchResultsRefetchableQuery.graphql";
import {
    templatePaginatedSearchResultsRelayRefetchableFragment$data,
    templatePaginatedSearchResultsRelayRefetchableFragment$key
} from "@/__generated__/templatePaginatedSearchResultsRelayRefetchableFragment.graphql";

import { TemplateCardGrid } from "../components/template-card-grid-relay";

export const INITIAL_PAGE_SIZE = 9;

function hasPage(
    pages: templatePaginatedSearchResultsRelayRefetchableFragment$data[],
    page: templatePaginatedSearchResultsRelayRefetchableFragment$data
) {
    return pages.some(
        (p) =>
            p.templates.edges.length === page.templates.edges.length &&
            p.templates.pageInfo.endCursor === page.templates.pageInfo.endCursor
    );
}

type SortLookup = {
    [templateUrlSlug: string]: number;
};

function sortBy(
    items: templatePaginatedSearchResultsRelayRefetchableFragment$data["templates"]["edges"],
    sortBy?: "name" | SortLookup | null
): templatePaginatedSearchResultsRelayRefetchableFragment$data["templates"]["edges"] {
    if (!sortBy) {
        return [...items];
    }
    if (sortBy === "name") {
        return [...items].sort(({ node: a }, { node: b }) =>
            a.name && b.name ? a.name.localeCompare(b.name) : a.name ? -1 : 1
        );
    }

    return [...items].sort(({ node: a }, { node: b }) => {
        const aRank = a.urlSlug ? sortBy[a.urlSlug] : undefined;
        const bRank = b.urlSlug ? sortBy[b.urlSlug] : undefined;
        // Sort by rank 1 is higher than 2
        // if aRank is undefined, it should be sorted lower than bRank
        if (aRank === undefined) {
            return bRank === undefined ? 0 : 1;
        }
        if (bRank === undefined) {
            return -1;
        }
        return aRank - bRank;
    });
}

function dataToPages(
    data: templatePaginatedSearchResultsRelayRefetchableFragment$data,
    sortLookup?: "name" | SortLookup | null
): templatePaginatedSearchResultsRelayRefetchableFragment$data[] {
    if (data.templates?.edges.length > INITIAL_PAGE_SIZE) {
        const sortedEdges = sortBy(data.templates.edges, sortLookup);
        const initialEdges = sortedEdges.slice(0, INITIAL_PAGE_SIZE);
        const additionalEdges = sortedEdges.slice(INITIAL_PAGE_SIZE);
        return [
            {
                ...data,
                templates: {
                    ...data.templates,
                    edges: initialEdges
                }
            },
            {
                ...data,
                templates: {
                    ...data.templates,
                    edges: additionalEdges
                }
            }
        ];
    }

    return [data];
}

export type TemplatePaginatedSearchResultsProps = {
    initialSearchResults: templatePaginatedSearchResultsRelayRefetchableFragment$key;
    /**
     * `searchQuery` is required to be able to fetch additional pages
     * of results. Because CPAPI does not separate pagination
     * arguments from search arguments, it is not possible to query
     * pagination without reconstructing the entire query arguments.
     */
    searchQuery: ContentPlatformContentQueryInput;
    sortLookup?: "name" | SortLookup | null;
    wasPreloaded?: boolean;
};

export const TemplatePaginatedSearchResultsRelayRefetchableFragment = graphql`
    fragment templatePaginatedSearchResultsRelayRefetchableFragment on Query
    @refetchable(queryName: "templatePaginatedSearchResultsRefetchableQuery")
    @argumentDefinitions(
        searchQuery: { type: "ContentPlatformContentQueryInput!" }
    ) {
        templates(search: { queries: [$searchQuery] }) {
            edges {
                node {
                    templateId
                    name
                    urlSlug
                    ...templateCardRelayFragment
                }
            }
            pageInfo {
                hasNextPage
                endCursor
            }
        }
    }
`;

export const TemplatePaginatedSearchResults = ({
    initialSearchResults,
    searchQuery,
    sortLookup = "name",
    wasPreloaded = false
}: TemplatePaginatedSearchResultsProps) => {
    const [loadMorePending, startLoadMore] = useTransition();
    const [currentPage, setCurrentPage] = useState(1);

    const [data, refetchFragment] = useRefetchableFragment(
        TemplatePaginatedSearchResultsRelayRefetchableFragment,
        initialSearchResults
    );

    /** bookkeeping to detect when the results should be reset */
    const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
    const [prevData, setPrevData] = useState(data);

    /** A ref to the latest refetch request. */
    const refetchRef = useRef<ReturnType<typeof refetchFragment>>(null);

    const [paginatedSearchResults, setPaginatedSearchResults] = useState(() =>
        dataToPages(data, sortLookup)
    );

    const resetSearchResults = useCallback(
        (
            data?: templatePaginatedSearchResultsRelayRefetchableFragment$data
        ) => {
            setCurrentPage(1);
            setPaginatedSearchResults(
                data ? dataToPages(data, sortLookup) : []
            );
        },
        [sortLookup]
    );

    const addSearchResults = useCallback(
        (data: templatePaginatedSearchResultsRelayRefetchableFragment$data) => {
            const pagesToAdd = dataToPages(data, sortLookup).filter(
                (newPage) => !hasPage(paginatedSearchResults, newPage)
            );
            if (pagesToAdd.length > 0) {
                setPaginatedSearchResults((prev) => [...prev, ...pagesToAdd]);
            }
        },
        [paginatedSearchResults, sortLookup]
    );

    const currentSearchResults = useMemo(() => {
        const hasNextPage = currentPage < paginatedSearchResults.length;
        return paginatedSearchResults.slice(0, currentPage).reduce(
            (acc, { templates }) => ({
                ...acc,
                ...templates,
                edges: [...acc.edges, ...templates.edges],
                pageInfo: {
                    ...acc.pageInfo,
                    hasNextPage: hasNextPage || templates.pageInfo.hasNextPage,
                    endCursor: hasNextPage ? null : templates.pageInfo.endCursor
                }
            }),
            {
                edges: [],
                pageInfo: { hasNextPage, endCursor: null }
            } as templatePaginatedSearchResultsRelayRefetchableFragment$data["templates"]
        );
    }, [currentPage, paginatedSearchResults]);

    const loadMore = useCallback(() => {
        if (currentSearchResults?.pageInfo.hasNextPage && searchQuery) {
            setCurrentPage((prev) => prev + 1);
            if (currentSearchResults?.pageInfo.endCursor != null) {
                startLoadMore(() => {
                    refetchRef.current?.dispose();
                    // @ts-ignore
                    refetchRef.current = refetchFragment({
                        searchQuery: {
                            ...searchQuery,
                            after: currentSearchResults.pageInfo.endCursor
                        }
                    });
                });
            }
        }
    }, [
        currentSearchResults.pageInfo.endCursor,
        currentSearchResults.pageInfo.hasNextPage,
        refetchFragment,
        searchQuery
    ]);

    // Reset the search results when the search query changes,
    // or add search results when the data changes.
    useEffect(() => {
        if (prevSearchQuery !== searchQuery) {
            setPrevSearchQuery(searchQuery);
            setPrevData(data);
            resetSearchResults(data);
        } else if (prevData !== data) {
            setPrevData(data);
            addSearchResults(data);
        }
    }, [
        addSearchResults,
        data,
        prevData,
        prevSearchQuery,
        resetSearchResults,
        searchQuery
    ]);

    // TODO: Figure out if this is the right place to do this.
    // Reinitialize tracking when displayed templates changes.
    useEffect(() => {
        Initializer.autoTrack();
    }, [currentSearchResults]);

    return (
        <TemplateCardGrid
            templates={currentSearchResults}
            onLoadMore={loadMore}
            initialAnimation={!wasPreloaded}
            loadMorePending={loadMorePending}
        />
    );
};
